import {Ref, shallowRef, ShallowRef} from "vue";
import EventManager from "~/ts/library/EventManager";
import Dictionary from "~/ts/library/Dictionary";
import ObjectHelper from "~/ts/library/ObjectHelper";
import {FacingMode} from "~/ts/library/webRtc/VideoFacingMode";
import Delay from "~/ts/library/Delay";
import WebRtcCallLoggerInterface from "~/ts/library/webRtc/WebRtcCallLoggerInterface";
import {BackgroundBlurEffect} from "~/ts/library/webRtc/BackgroundBlurEffect";

const NEW_STREAM = "newStream";

let checkedDeviceIds: Dictionary<Dictionary<boolean>> = {};

export default class WebRtcMediaStream {
    private audioEnabled: Ref<boolean>;
    private webCamEnabled: Ref<boolean>;
    private selfieSegmentationEnabled: Ref<boolean>;
    private screenShareEnabled: Ref<boolean>;
    private mediaStream: ShallowRef<MediaStream | undefined>;
    private screenShareStream?: MediaStream;
    private microphoneStream?: MediaStream;
    private webCamStream?: MediaStream;
    private webcamBlurEffect?: BackgroundBlurEffect;
    private webCamStreamAudio: boolean = true;
    private initializePromise?: Promise<void>;
    private eventManager: EventManager;
    private selectedAudioOutputDeviceId?: string;
    private lastVideoFacingMode?: FacingMode;
    private isMobile: boolean;
    private logger: WebRtcCallLoggerInterface;

    constructor(isMobile: boolean, webCamEnabled: Ref<boolean>, selfieSegmentationEnabled: Ref<boolean>, audioEnabled: Ref<boolean>, screenShareEnabled: Ref<boolean>, logger: WebRtcCallLoggerInterface) {
        this.logger = logger;
        this.mediaStream = shallowRef();
        this.isMobile = isMobile;
        this.audioEnabled = audioEnabled;
        this.webCamEnabled = webCamEnabled;
        this.selfieSegmentationEnabled = selfieSegmentationEnabled;
        this.screenShareEnabled = screenShareEnabled;
        this.eventManager = new EventManager();
        (async () => {
            let device = (await WebRtcMediaStream.getDevices(logger, ["audiooutput"]))[0];
            if (device) {
                this.audioOutputDeviceId = device.deviceId;
            }
        })();
    }


    public onMediaStreamChange(callback: () => void) {
        return this.eventManager.addEventListener(NEW_STREAM, callback);
    }

    public getMediaStream(): MediaStream | undefined {
        return this.mediaStream.value;
    }

    public async initializeStreamIfNotInitialized(): Promise<void> {
        if (!this.getMediaStream()) {
            if (this.initializePromise) {
                return await this.initializePromise;
            }
        }
        await this.initializeStream();
    }

    private lastInitializedDeviceIds: {
        audioinput?: string,
        videoinput?: string
    } = {
        audioinput: undefined,
        videoinput: undefined
    };

    private async getLastInitializedDeviceId(kind: "audioinput" | "videoinput") {
        this.logger.addLog('{getLastInitializedDeviceId} start', kind);
        let result = this.lastInitializedDeviceIds[kind];
        if (!result) {
            this.logger.addLog('{getLastInitializedDeviceId} !result');
            let devices = await WebRtcMediaStream.getDevices(this.logger, [kind]);
            this.logger.addLog('{getLastInitializedDeviceId} devices = ', devices);
            let bluetoothDeviceId: string | undefined;
            let speakerPhoneDeviceId: string | undefined;
            for (let device of devices) {
                if (device.label.toLowerCase().includes("speakerphone")) {
                    speakerPhoneDeviceId = device.deviceId;
                } else if (device.label.toLowerCase().includes("bluetooth")) {
                    bluetoothDeviceId = device.deviceId;
                }
            }
            if (!bluetoothDeviceId && speakerPhoneDeviceId) {
                result = speakerPhoneDeviceId;
            } else {
                result = devices[0]?.deviceId;
            }
        }
        this.logger.addLog('{getLastInitializedDeviceId} result', result);
        return result;
    }

    public async initializeStream(payload?: {
        videoInputDeviceId?: string,
        audioInputDeviceId?: string,
        videoFacingMode?: FacingMode,
    }): Promise<void> {
        this.logger.addLog('{initializeStream} start', payload);
        if (!payload) {
            payload = {};
        }
        //console.log("initializeStream", payload);
        if (!payload.videoFacingMode) {
            payload.videoFacingMode = this.lastVideoFacingMode;
        } else {
            this.lastVideoFacingMode = payload.videoFacingMode;
        }
        if (!payload.audioInputDeviceId) {
            this.logger.addLog('{initializeStream} !payload.audioInputDeviceId');
            payload.audioInputDeviceId = await this.getLastInitializedDeviceId("audioinput");
            this.logger.addLog('{initializeStream} done audioinput');
        }
        if (!payload.videoInputDeviceId) {
            this.logger.addLog('{initializeStream} !payload.videoInputDeviceId');
            payload.videoInputDeviceId = await this.getLastInitializedDeviceId("videoinput");
            this.logger.addLog('{initializeStream} done videoinput');
        }
        if (this.initializePromise) {
            this.logger.addLog('{initializeStream} start initializePromise');
            await this.initializePromise;
            this.logger.addLog('{initializeStream} done initializePromise');
        }
        let outerResolve: (() => void) | undefined;
        this.initializePromise = new Promise(resolve => outerResolve = resolve);
        if (!this.audioEnabled.value) {
            this.stopStream(this.microphoneStream);
            this.microphoneStream = undefined;
        }
        while (true) {
            this.stopStream(this.screenShareStream, "video");
            this.stopStream(this.webCamStream, "video");
            //this.stopStream(this.microphoneStream);
            //this.microphoneStream = null;
            this.screenShareStream = undefined;
            this.webCamStream = undefined;

            if (this.screenShareEnabled.value) {
                this.logger.addLog('{initializeStream} screenShareEnabled');
                let error: any;
                try {
                    await this.mixWithMicrophoneStreamAndApply((await this.getScreenShareStream())!, payload?.audioInputDeviceId);

                } catch (e) {
                    this.logger.addLog('{initializeStream} error', e);
                    console.error(e);
                    error = e;
                }

                if (error) {
                    this.screenShareEnabled.value = false;
                    continue;
                }
            } else if (this.webCamEnabled.value) {
                try {
                    await this.mixWithMicrophoneStreamAndApply((await this.getWebCamStream(payload?.videoInputDeviceId, payload.videoFacingMode))!, payload?.audioInputDeviceId);
                } catch (e) {
                    console.error(e);
                    this.logger.addLog('{initializeStream} error', e);
                    this.webCamEnabled.value = false;
                    continue;
                }
            } else if (this.audioEnabled.value) {
                this.logger.addLog('{this.audioEnabled.value} with empty stream');
                try {
                    await this.mixWithMicrophoneStreamAndApply(this.getEmptyStream(), payload?.audioInputDeviceId);
                } catch (e) {
                    console.error(e);
                    this.logger.addLog('{initializeStream} error', e);
                    this.audioEnabled.value = false;
                    continue;
                }
            } else {
                try {
                    this.logger.addLog('empty stream');
                    this.applyStream(this.getEmptyStream());
                } catch (e) {
                    this.logger.addLog('{initializeStream} error', e);
                    console.error(e);
                }
            }
            break;
        }
        this.initializePromise = undefined;
        if (this.audioInputDeviceId) {
            this.lastInitializedDeviceIds.audioinput = this.audioInputDeviceId;
        }
        if (this.videoInputDeviceId) {
            this.lastInitializedDeviceIds.videoinput = this.videoInputDeviceId;
        }
        if (outerResolve) {
            outerResolve();
        }
        this.logger.addLog('{initializeStream} done');
    }

    private async mixWithMicrophoneStreamAndApply(stream: MediaStream, audioInputDeviceId?: string) {
        this.logger.addLog('{mixWithMicrophoneStreamAndApply} start');
        stream.getAudioTracks().forEach(track => {
            stream.removeTrack(track);
        })
        if (this.audioEnabled.value) {
            this.logger.addLog('{mixWithMicrophoneStreamAndApply} audioEnabled');
            try {
                let microphoneStream = await this.getMicrophoneStream(audioInputDeviceId);
                let track = microphoneStream!.getAudioTracks()[0];
                if (track) {
                    stream.addTrack(track);
                }
            } catch (e) {
                this.logger.addLog('{mixWithMicrophoneStreamAndApply} error', e);
                this.logger.addLog('{mixWithMicrophoneStreamAndApply} создаем пустой стрим');

                let emptyStream = this.getEmptyAudioStream();
                let track = emptyStream.getAudioTracks()[0];
                if (track) {
                    this.logger.addLog('{mixWithMicrophoneStreamAndApply} получили пустой трэк, применяем');
                    stream.addTrack(track);
                }

                this.audioEnabled.value = false;
            }
        }
        this.applyStream(stream);
    }

    private applyStream(stream: MediaStream) {
        this.mediaStream.value = stream;
        this.eventManager.emit(NEW_STREAM, stream);
    }

    private isStreamActive(stream?: MediaStream): boolean {
        return stream?.active === true;
    }

    private createDeviceIdsByDeviceId(deviceId?: string) {
        let result: (string | undefined)[] = [];
        if (deviceId) {
            result.push(deviceId);
        }
        result.push(undefined);
        return result;
    }

    private static getUserMediaLocked: boolean = false

    public static async getUserMedia(logger: WebRtcCallLoggerInterface, constraints?: MediaStreamConstraints, stopStream: boolean = false): Promise<MediaStream> {
        return new Promise<MediaStream>(async (resolve, reject) => {
            let timeoutId: any;

            timeoutId = setTimeout(async () => {
                this.getUserMediaLocked = false;
                reject(new Error("Пользователь не дал разрешение"));
            }, 60000);
            logger.addLog('{getUserMedia} start', constraints, stopStream);
            let lockSuccessFull = false;
            while (!lockSuccessFull) {
                lockSuccessFull = !this.getUserMediaLocked;
                if (lockSuccessFull) {
                    this.getUserMediaLocked = true;
                } else {
                    await Delay.make(10);
                }
            }
            logger.addLog('{getUserMedia} lockSuccessFull');
            let error: Error | undefined;
            let result: MediaStream | undefined;
            try {
                logger.addLog('{getUserMedia}  navigator.mediaDevices.getUserMedia', constraints);
                result = await navigator.mediaDevices.getUserMedia(constraints);
                clearTimeout(timeoutId);
                logger.addLog('{getUserMedia}  navigator.mediaDevices.getUserMedia done');
                if (stopStream) {
                    for (let item of result.getTracks()) {
                        item.stop();
                    }
                }
            } catch (e) {
                clearTimeout(timeoutId);
                error = e instanceof Error ? e : new Error(String(e));
                logger.addLog('{getUserMedia} error', e);
            }

            this.getUserMediaLocked = false;
            if (error) {
                reject(error);
            }

            resolve(result!);
        });

    }

    private lastAudioInputDeviceId?: string;

    private async getMicrophoneStream(audioInputDeviceId?: string): Promise<MediaStream | undefined> {
        if (!this.isStreamActive(this.microphoneStream) || (audioInputDeviceId && audioInputDeviceId != this.lastAudioInputDeviceId)) {
            this.logger.addLog('{getMicrophoneStream} start');
            this.lastAudioInputDeviceId = audioInputDeviceId;
            this.stopStream(this.microphoneStream);
            let deviceIds = this.createDeviceIdsByDeviceId(audioInputDeviceId);
            for (let deviceId of deviceIds) {
                this.logger.addLog('{getMicrophoneStream} deviceId', deviceId);
                try {
                    this.microphoneStream = await WebRtcMediaStream.getUserMedia(this.logger, {
                        audio: {
                            deviceId,
                            autoGainControl: true,
                            echoCancellation: true
                        }
                    });
                    break;
                } catch (e) {
                    this.logger.addLog('{getMicrophoneStream} error', e);
                    if (!deviceId) {
                        throw e;
                    }
                }
            }
            this.logger.addLog('{getMicrophoneStream} done');
        }
        return this.microphoneStream;
    }

    private static async checkDevice(kind: "videoinput" | "audioinput", device: MediaDeviceInfo, logger: WebRtcCallLoggerInterface): Promise<boolean> {
        logger.addLog('{checkDevice} start', kind, device);
        let result: boolean;
        if (!checkedDeviceIds[kind]) {
            checkedDeviceIds[kind] = {};
        }
        if (!checkedDeviceIds[kind].hasOwnProperty(device.deviceId)) {

            try {
                if (kind == "videoinput") {
                    await WebRtcMediaStream.getUserMedia(logger, {
                        video: {deviceId: device.deviceId}
                    }, true);
                } else if (kind == "audioinput") {
                    await WebRtcMediaStream.getUserMedia(logger, {
                        audio: {deviceId: device.deviceId}
                    }, true);
                }
                result = true;
            } catch (e) {
                result = false;
            }
            if (result || ObjectHelper.hasKeys(checkedDeviceIds[kind])) {
                checkedDeviceIds[kind][device.deviceId] = result
            }
        } else {
            result = !!checkedDeviceIds[kind][device.deviceId];
        }
        return result;
    }

    public static async getDevices(logger: WebRtcCallLoggerInterface, kinds: MediaDeviceKind[], validateByGetUserMedia: boolean = false): Promise<MediaDeviceInfo[]> {
        logger.addLog('{getDevices} start', kinds);
        let result: MediaDeviceInfo[] = [];
        let ignoredIds = ["communications"];
        if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
            try {
                logger.addLog('{getDevices} navigator.mediaDevices.enumerateDevices');
                let devices = await navigator.mediaDevices.enumerateDevices();
                logger.addLog('{getDevices} navigator.mediaDevices.enumerateDevices result is', devices);
                for (let device of devices) {
                    if (kinds.includes(device.kind)) {
                        if (!ignoredIds.includes(device.deviceId)) {
                            try {
                                if (validateByGetUserMedia) {
                                    //Проверяем работоспособность
                                    if (device.kind == "videoinput"/* || device.kind == "audioinput"*/) {
                                        logger.addLog('{getDevices} start checkDevice', device);
                                        if (!(await this.checkDevice(device.kind, device, logger))) {
                                            continue;
                                        }
                                    }
                                }
                                result.push(device);
                            } catch (e) {

                            }
                        }
                    }
                }
            } catch (e) {
                logger.addLog('{getDevices} error', e);
            }
        }
        logger.addLog('{getDevices} result', result);
        return result;
    }

    public isCanChangeAudioOutput(): boolean {
        let video: HTMLVideoElement = document.createElement("video");
        return !!(video.setSinkId);
    }

    public connectVideoAndAudioContext(video: HTMLVideoElement): () => void {
        return this.eventManager.addEventListener("newAudioOutputDeviceId", id => {
            if (video.setSinkId) {
                video.setSinkId(id);
            }
        });
    }

    get audioInputDeviceId(): string | undefined {
        for (let stream of [this.webCamStream, this.microphoneStream]) {
            if (stream) {
                let track = stream.getAudioTracks()[0];
                if (track && typeof track.getCapabilities == "function") {
                    let deviceId = track.getCapabilities().deviceId;
                    if (deviceId) {
                        return deviceId;
                    }
                }
            }
        }
        return undefined;
    }

    set audioInputDeviceId(id: string) {
        this.initializeStream({videoInputDeviceId: this.videoInputDeviceId, audioInputDeviceId: id});
    }


    get videoInputDeviceId(): string | undefined {
        let track = this.webCamStream?.getVideoTracks()[0];
        if (track && typeof track.getCapabilities == "function") {
            return track.getCapabilities().deviceId;
        }
        return undefined;
    }

    set videoInputDeviceId(id: string) {
        this.initializeStream({videoInputDeviceId: id, audioInputDeviceId: this.audioOutputDeviceId});
    }

    get audioOutputDeviceId(): string | undefined {
        return this.selectedAudioOutputDeviceId;
    }

    set audioOutputDeviceId(id: string) {
        this.selectedAudioOutputDeviceId = id;
        this.eventManager.emit("newAudioOutputDeviceId", id);
    }

    public static async getVideoInputDevices(logger: WebRtcCallLoggerInterface): Promise<MediaDeviceInfo[]> {
        return await this.getDevices(logger, ["videoinput"]);
    }

    private async getWebCamStream(videoDeviceId?: string, videoFacingMode?: FacingMode): Promise<MediaStream | undefined> {
        let audio = this.audioEnabled.value;
        let streamToStop: MediaStream | undefined;
        if (audio != this.webCamStreamAudio || videoDeviceId) {
            streamToStop = this.webCamStream;
        }
        this.webCamStreamAudio = audio;
        if (streamToStop || !this.isStreamActive(this.webCamStream)) {
            this.logger.addLog('{getWebCamStream} start', videoDeviceId, videoFacingMode);
            let audioModes: boolean[] = [audio];
            if (audio) {
                audioModes.push(false);
            }
            let tryCount = 0;
            let success = false;
            for (let audioMode of audioModes) {
                this.logger.addLog('{getWebCamStream} audioMode', audioMode);
                tryCount++;
                try {
                    let videoDeviceIds = this.createDeviceIdsByDeviceId(videoDeviceId);
                    //let audioDeviceIds = this.createDeviceIdsByDeviceId(audioDeviceId);

                    let index = 0;
                    for (let videoDeviceId of videoDeviceIds) {
                        this.logger.addLog('{getWebCamStream} videoDeviceId', videoDeviceId);
                        if (success) {
                            break;
                        }
                            index++;
                            try {
                                let webCamStream = await WebRtcMediaStream.getUserMedia(this.logger, {
                                    video: {
                                        width: {
                                            max: 1280,
                                            ideal: 1280
                                        },
                                        height: {
                                            max: 1280,
                                            ideal: 720
                                        },
                                        frameRate: 30,
                                        deviceId: this.isMobile ? undefined : videoDeviceId,
                                        facingMode: this.isMobile ? videoFacingMode : undefined
                                    }
                                });
                                if (this.selfieSegmentationEnabled.value && this.logger.isSelfieSegmentationAvailable()) {
                                    this.webcamBlurEffect = new BackgroundBlurEffect({
                                        modelSelection: 0,
                                        blurRadius: 15,
                                        segmentationThreshold: 1,
                                        modelRootPath: this.logger.getSelfieSegmentationModelRootPath()
                                    });
                                    this.webCamStream = await this.webcamBlurEffect.createBlurredStream(webCamStream);
                                } else {
                                    this.webCamStream = webCamStream;
                                }

                                success = true;
                                break;
                            } catch (e) {
                                this.logger.addLog('{getWebCamStream} error', e);
                                if (index == videoDeviceIds.length) {
                                    throw e;
                                }
                            }
                    }
                    if (tryCount == 2) {
                        this.audioEnabled.value = this.webCamStreamAudio = audioMode;
                    }
                    break;
                } catch (e) {
                    this.logger.addLog('{getWebCamStream} error', e);
                    if (!audioMode) {
                        throw e;
                    }
                }
            }
        }
        let result = this.webCamStream;
        if (streamToStop) {
            setTimeout(() => {
                this.stopStream(streamToStop, "video");
            }, 1000);
        }
        return result;
    }

    private async getScreenShareStream(): Promise<MediaStream | undefined> {
        if (!this.isStreamActive(this.screenShareStream)) {
            this.logger.addLog('{getScreenShareStream} start');
            this.screenShareStream = await navigator.mediaDevices.getDisplayMedia({
                video: {
                    displaySurface: "screen",
                    width: {
                        max: 1920
                    },
                    height: {
                        max: 1920
                    },
                    frameRate: 30
                }
            });
            this.logger.addLog('{getScreenShareStream} done');
        }
        return this.screenShareStream;
    }

    private getEmptyStream(): MediaStream {
        let canvas = document.createElement("canvas");
        canvas.width = 16;
        canvas.height = 9;
        const ctx = canvas.getContext("2d")!;

        ctx.rect(0, 0, canvas.width, canvas.height);

        setInterval(function () {
            ctx.fillStyle = "black";
            ctx.fill();
        }, 1000);


        return canvas.captureStream(1);
    }

    private getEmptyAudioStream(): MediaStream {
        const ctx = new AudioContext();
        const silence = ctx.createGain();
        silence.gain.value = 0; // отключаем звук
        const destination = ctx.createMediaStreamDestination();
        silence.connect(destination);
        // Создаем источник, который ничего не издает
        const oscillator = ctx.createOscillator();
        oscillator.connect(silence);
        oscillator.start();
        oscillator.stop(); // сразу останавливаем, чтобы не было звука
        return destination.stream;
    }

    private stopStream(stream?: MediaStream, kind?: "audio" | "video") {
        if (stream) {
            let tracks = stream.getTracks();
            if (kind == "audio") {
                tracks = stream.getAudioTracks();
            } else if (kind == "video") {
                tracks = stream.getVideoTracks();
            }
            for (let track of tracks) {
                track.stop();
                stream.removeTrack(track);
            }
            if (stream === this.webCamStream) {
                this.webcamBlurEffect?.stop();
                this.webcamBlurEffect = undefined;
            }
        }
    }

    public destroy() {
        let streams = [this.screenShareStream, this.microphoneStream, this.webCamStream, this.mediaStream.value];
        for (let stream of streams) {
            if (stream) {
                this.stopStream(stream);
            }
        }
        this.eventManager.removeAllEventListeners();
    }
}